if (!customElements.get('trumoto-product-story')) {
  customElements.define('trumoto-product-story', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced = reduced;
      const desktop = matchMedia('(min-width: 901px)');
      this.portrait = matchMedia('(max-width: 749px) and (orientation: portrait)');
      this.videoControls = new Map();
      this.themeImage = this.querySelector('[data-theme-image]');
      this.themeButtons = [...this.querySelectorAll('[data-theme-src]')];
      this.themeIndex = Math.max(0, this.themeButtons.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
      this.themeAppliedIndex = this.themeIndex;
      this.themeSequence = 0;
      this.themeImages = new Map();
      this.themeButtons.forEach((button, index) => button.addEventListener('click', () => this.selectTheme(index), options));
      this.themeMobile = matchMedia('(max-width: 900px), (pointer: coarse)');
      const themeStage = this.querySelector('.tm-story__selected-theme');
      themeStage.addEventListener('pointerdown', event => {
        if (!this.themeMobile.matches || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.themeGesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        themeStage.setPointerCapture(event.pointerId);
      }, options);
      themeStage.addEventListener('pointerup', event => {
        const start = this.themeGesture;
        this.themeGesture = null;
        if (!start || start.id !== event.pointerId || !this.themeMobile.matches) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        const rtl = getComputedStyle(themeStage).direction === 'rtl';
        const direction = (dx < 0 ? 1 : -1) * (rtl ? -1 : 1);
        this.selectTheme(this.themeIndex + direction, dx < 0 ? 1 : -1);
      }, options);
      const cancelThemeGesture = () => { this.themeGesture = null; };
      themeStage.addEventListener('pointercancel', cancelThemeGesture, options);
      themeStage.addEventListener('lostpointercapture', cancelThemeGesture, options);
      this.preloadThemeNeighbors();
      this.mediaObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (!entry.isIntersecting) {
            video.pause();
            this.videoControls.get(video)?.hide();
            video.closest('trumoto-intro-film')?.controls?.hide();
            return;
          }
          this.prepareVideo(video);
          if (this.dataset.video === 'true' && !reduced.matches && !video.hasAttribute('data-intro-complete')) video.play().catch(error => {
            if (error.name === 'AbortError') return;
            video.setAttribute('data-intro-blocked', '');
            video.closest('trumoto-intro-film')?.playFailed?.();
          });
        });
      }, { threshold: .25 });
      this.querySelectorAll('video').forEach(video => {
        if (!video.closest('trumoto-intro-film')) this.videoControls.set(video, new window.TruMotoVideoControls(video, options));
        this.mediaObserver.observe(video);
      });
      this.portrait.addEventListener('change', () => {
        this.querySelectorAll('video[data-portrait-src]').forEach(video => {
          if (!video.querySelector('source[src]')) return;
          const playing = !video.paused;
          this.prepareVideo(video);
          if (playing && !reduced.matches) video.play().catch(() => {});
        });
      }, options);
      const scenes = [...this.querySelectorAll('[data-scene]')];
      this.activeScenes = new Set();
      this.sceneObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) this.activeScenes.add(entry.target); else this.activeScenes.delete(entry.target);
      }), { rootMargin: '100px' });
      scenes.forEach(scene => this.sceneObserver.observe(scene));
      let frame = 0;
      const update = () => {
        frame = 0;
        if (reduced.matches || !desktop.matches || this.dataset.motion !== 'true') return;
        this.activeScenes.forEach(scene => {
          const bounds = scene.getBoundingClientRect();
          const progress = Math.max(0, Math.min(1, (innerHeight - bounds.top) / (innerHeight + bounds.height)));
          const visual = scene.querySelector('.tm-story__visual');
          visual.style.setProperty('--tm-rise', `${(progress - .5) * -60}px`);
          visual.style.setProperty('--tm-scale', String(.96 + Math.sin(progress * Math.PI) * .04));
        });
      };
      const scroll = () => { if (!frame) frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', scroll, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', scroll, options);
      reduced.addEventListener('change', () => {
        this.querySelectorAll('video').forEach(video => video.pause());
        scenes.forEach(scene => { const visual=scene.querySelector('.tm-story__visual'); visual.style.removeProperty('--tm-rise'); visual.style.removeProperty('--tm-scale'); });
      }, options);
      this.cancelFrame = () => cancelAnimationFrame(frame);
      update();
    }
    themeAsset(index) {
      if (!this.themeImages.has(index)) {
        const next = new Image();
        next.src = this.themeButtons[index].dataset.themeSrc;
        const loaded = next.decode().then(() => next);
        this.themeImages.set(index, loaded);
        loaded.catch(() => this.themeImages.delete(index));
      }
      return this.themeImages.get(index);
    }
    preloadThemeNeighbors() {
      const count = this.themeButtons.length;
      for (const offset of [-1, 1]) this.themeAsset((this.themeIndex + offset + count) % count).catch(() => {});
    }
    async selectTheme(index, direction = 0) {
      const count = this.themeButtons.length;
      const target = (index + count) % count;
      const current = ++this.themeSequence;
      this.themeIndex = target;
      let next;
      try { next = await this.themeAsset(target); }
      catch { if (current === this.themeSequence) this.themeIndex = this.themeAppliedIndex; return; }
      if (current !== this.themeSequence || !this.isConnected) return;
      const button = this.themeButtons[target];
      this.themeImage.src = next.src;
      this.themeImage.alt = `${button.dataset.themeTitle} dashboard`;
      this.querySelector('[data-theme-name]').textContent = button.dataset.themeTitle;
      this.querySelector('[data-theme-description]').textContent = button.dataset.themeCopy;
      this.themeButtons.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
      this.themeAppliedIndex = target;
      this.themeAnimation?.cancel();
      if (!this.reduced.matches) this.themeAnimation = this.themeImage.animate([
        { opacity: .35, transform: direction ? `translateX(${direction * 28}px)` : 'scale(.97)' },
        { opacity: 1, transform: 'none' }
      ], { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)' });
      this.preloadThemeNeighbors();
    }
    prepareVideo(video) {
      const source = video.querySelector('source');
      if (!source) return;
      if (video.dataset.portraitSrc) {
        const portrait = this.portrait.matches;
        const src = portrait ? video.dataset.portraitSrc : video.dataset.landscapeSrc;
        video.poster = portrait ? video.dataset.portraitPoster : video.dataset.landscapePoster;
        if (source.getAttribute('src') === src) return;
        source.src = src;
      } else if (source.hasAttribute('data-src')) source.src = source.dataset.src;
      else return;
      source.removeAttribute('data-src');
      video.load();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.mediaObserver?.disconnect();
      this.sceneObserver?.disconnect();
      this.cancelFrame?.();
      this.themeAnimation?.cancel();
      this.themeGesture = null;
      this.querySelectorAll('video').forEach(video => video.pause());
      this.ready = false;
    }
  });
}
